import { KatalComponent, Keys } from '../../shared/base';

/**
 * @component {kat-pagination} KatalPagination Controls to allow users to move between pages of data
 * @example Default {"total-items": "1000", "items-per-page": "10", "page":"4"}
 * @event page-changed This is fired when the selected page of the component changes either clicking a specific page, or the navigators
 * @status Production
 * @theme flo
 */
export class KatalPagination extends KatalComponent {
  static get observedAttributes() {
    return ['total-items', 'items-per-page', 'page', 'katal-1-2-spacing-fixes'];
  }

  constructor() {
    super(KatalPagination.observedAttributes);
    this.addEventListener('click', e => {
      this.handleChangeEvent(e);
    });

    this.addEventListener('keydown', e => {
      if (e.keyCode === Keys.Enter || e.keyCode === Keys.Space) {
        this.handleChangeEvent(e);
      }
    });
  }

  connectedCallback() {
    super.connectedCallback();
    this.rebuildList();
  }

  handleChangeEvent(e) {
    if (e.target.classList.contains('page')) {
      const pageClicked = e.target.innerText.trim();
      if (pageClicked != this.page) {
        this.page = pageClicked;
        this.pageChanged();
      }
    } else if (
      e.target.classList.contains('nav-left') ||
      e.target.classList.contains('kat-icon-pagination-left') ||
      e.target.parentElement.classList.contains('kat-icon-pagination-left')
    ) {
      if (this.page > 1) {
        this.page--;
        this.pageChanged();
      }
    } else if (
      e.target.classList.contains('nav-right') ||
      e.target.classList.contains('kat-icon-pagination-right') ||
      e.target.parentElement.classList.contains('kat-icon-pagination-right')
    ) {
      if (this.page + 1 <= this._totalPages) {
        this.page++;
        this.pageChanged();
      }
    }
  }

  /**
   * Attemps to parse a numeric value as an integer for an attribute.
   * If its not a valid number or 0, the default value is returned.
   */
  getOrDefaultNumber(attrName, defaultValue) {
    const attr = this.getAttribute(attrName);
    if (attr) {
      const parsedValue = parseInt(attr);
      return isNaN(parsedValue) ? defaultValue : parsedValue;
    }
    return defaultValue;
  }

  /**
   * @classprop {number} total-items This is the number of all items available for pagination
   * @required
   */
  get totalItems() {
    return this.getOrDefaultNumber('total-items', 1);
  }

  set totalItems(value) {
    this.setAttribute('total-items', value);
  }

  /**
   * @classprop {number} items-per-page The number of items that can be displayed in a single page.  Used to caculate togal pages
   * @required
   */
  get itemsPerPage() {
    return this.getOrDefaultNumber('items-per-page', 10);
  }

  set itemsPerPage(value) {
    this.setAttribute('items-per-page', value);
  }

  /**
   * @classprop {number} page The currently selected page
   * @required
   */
  get page() {
    return this.getOrDefaultNumber('page', 1);
  }

  set page(value) {
    this.setAttribute('page', value);
  }

  // Note: For the spacing attribute we just need the JSON metadata not the actual getter and setter.V
  /**
   * @classprop {boolean} katal-1-2-spacing-fixes A special compatibility flag that when set removes extra margins around this component and sets its height to
   * the correct default. Note that this will become the default behavior in the next major release of Katal. Take a look at our service bulletin page for
   * more details.
   */
  get katal12SpacingFixes() {
    return this.getBooleanAttribute('katal-1-2-spacing-fixes');
  }

  set katal12SpacingFixes(value) {
    return this.setBooleanAttribute('katal-1-2-spacing-fixes', value);
  }

  rebuildList() {
    const MAX_PAGER_SEGMENTS = 7;
    const MAX_DIFF_FROM_ENDPOINTS = 4;
    this._totalPages = Math.ceil(this.totalItems / this.itemsPerPage);
    const currentPage = this.page;
    const leftNavClass =
      currentPage == 1 ? 'pagination-left-end' : 'pagination-left';
    const rightNavClass =
      currentPage == this._totalPages
        ? 'pagination-right-end'
        : 'pagination-right';
    const tabIndex = `tabindex="0"`;

    let list = `<ul><li ${tabIndex} class='nav nav-left'>
            <kat-icon class="kat-icon-pagination-left ${leftNavClass}" name="chevron-left" size="small"></kat-icon>
        </li>`;

    function generatePages(startPageInclusive, endPageExclusive) {
      //increment by one since we are saying this is exclusive
      endPageExclusive++;
      for (let i = startPageInclusive; i < endPageExclusive; i++) {
        const selectedClass = i == currentPage ? 'selected' : '';
        list += `<li ${tabIndex} class="page page-${i} ${selectedClass}">${i}</li>`;
      }
    }

    function generateEllipsis() {
      list += `<li class="ellipsis">...</li>`;
    }

    let leftEllipsis = false;
    let rightEllipsis = false;

    if (this._totalPages > MAX_PAGER_SEGMENTS) {
      //the left elipsis will be shown when there are more than 7 total pages and the user has clicked on the 5th page.
      leftEllipsis = currentPage > MAX_DIFF_FROM_ENDPOINTS;
      //the right elipsis will only be shown when there are more then 7 pages and the user is within 4 pages of the total number of pages
      rightEllipsis = this._totalPages - currentPage >= MAX_DIFF_FROM_ENDPOINTS;
    }

    if (!leftEllipsis && !rightEllipsis) {
      //neither ellipsis is set.  Write all the numbers out
      generatePages(1, this._totalPages);
    } else if (leftEllipsis && !rightEllipsis) {
      //Only the left ellipsis is being set.  The first number will always be there
      generatePages(1, 1);
      generateEllipsis();

      //next we need to write out up to the last 5 pages of numbers for
      generatePages(this._totalPages - 4, this._totalPages);
    } else if (!leftEllipsis && rightEllipsis) {
      //In this case we need to write out the first 5 numbers an ellipsis and the max number
      generatePages(1, 5);
      generateEllipsis();
      generatePages(this._totalPages, this._totalPages);
    } else {
      //In this case both ellipsis are being written.  We need the first number, ellipsis, currentpage -1, currentpage, currentpage+1, ellipsis, lastnumber
      generatePages(1, 1);
      generateEllipsis();
      generatePages(currentPage - 1, currentPage + 1);
      generateEllipsis();
      generatePages(this._totalPages, this._totalPages);
    }

    list += `<li ${tabIndex} class='nav nav-right'>
            <kat-icon class="kat-icon-pagination-right ${rightNavClass}" name="chevron-right" size="small"></kat-icon>
        </li></ul>`;

    this.innerHTML = list;
  }

  pageChanged() {
    this.dispatchCustomEvent('page-changed', { page: this.page });
  }

  // Respond to attribute changes.
  attributeChangedCallback() {
    this.rebuildList();
  }
}

KatalComponent.register('kat-pagination', KatalPagination);
